import { useEffect, useId, useState } from 'react';
import type * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Button } from '@/components/ui/button';
import { FormField, Input } from '@/components/ui/field';
import { Mono } from '@/components/ui/primitives';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  /** Same verb as the trigger. "Publish" leads to "Publish", never "Confirm". */
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
  destructive?: boolean;
  /**
   * When set, the admin must type this exact string first (06 §3). Reserved for
   * the actions that cannot be undone: deleting an admin, publishing a wizard
   * version, changing a partner tag.
   */
  typedConfirmation?: string;
  children?: React.ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  loading = false,
  destructive = false,
  typedConfirmation,
  children,
}: ConfirmDialogProps): React.JSX.Element {
  const [typed, setTyped] = useState('');
  const inputId = useId();

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const armed = !typedConfirmation || typed.trim() === typedConfirmation;

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px]" />
        <AlertDialogPrimitive.Content className="fixed top-1/2 left-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-card border border-line bg-surface shadow-2xl">
          <div className="px-5 pt-5">
            <AlertDialogPrimitive.Title className="text-sm font-semibold text-ink">
              {title}
            </AlertDialogPrimitive.Title>
            <AlertDialogPrimitive.Description asChild>
              <div className="mt-2 text-sm text-ink-soft">{description}</div>
            </AlertDialogPrimitive.Description>
          </div>

          {children ? <div className="px-5 pt-4">{children}</div> : null}

          {typedConfirmation ? (
            <div className="px-5 pt-4">
              <FormField
                label="Type to confirm"
                htmlFor={inputId}
                hint={`Enter ${typedConfirmation} exactly.`}
              >
                <Input
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={typedConfirmation}
                />
              </FormField>
              <p className="mt-2 text-xs text-ink-faint">
                Confirming <Mono>{typedConfirmation}</Mono>
              </p>
            </div>
          ) : null}

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-line bg-surface-sunken/60 px-5 py-3">
            <AlertDialogPrimitive.Cancel asChild>
              <Button variant="ghost" disabled={loading}>
                Cancel
              </Button>
            </AlertDialogPrimitive.Cancel>
            <Button
              variant={destructive ? 'danger' : 'primary'}
              loading={loading}
              disabled={!armed}
              onClick={() => {
                void onConfirm();
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
